import React, { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import Papa from "papaparse";
import { utils, write } from "xlsx";

import Menu from "../../../helpers/Menu";
import Button from "../../../helpers/Button";
import DateFilter from "../../../common/DateFilter";
import { DropDownField, TextInputField, AutoCompleteField, MultiSelectField } from "../../../helpers";

import { searchVendorRateContractItems } from "../../../services/api/vendorRateContractItem";

import { raiseToast } from "../../../services/redux/reducers/toast/toast";
import { fetchBuyerCompanies, fetchBuyerCompaniesUsers } from "../../../services/redux/reducers/commonThunk";
import { fetchVendorRateContractItems } from "../../../services/redux/reducers/vendorRateContractItems/vendorRateContractItems";
import { updateSearchParams, updateSearchOptions, setSearchConfig } from "../../../services/redux/reducers/vendorRateContractItems/vendorRateContractItems";

import { getMatchedRegexPattern, getDateRange, normalizeLabel, getAddFilterOptions } from "../../../utils";
import { getMaterialMasterAndTempMaterialIds } from "../../../services/api/materialMaster";


let vendorRateContractItemsConfig = [
    {
        srNO: 1,
        static: true,
        type: "SELECT",
        name: "dataType",
        label: "Data type",
        options: [
            { label: 'Vendor Rate Contract Items Data', value: 'vendorRateContractItemsData' }
        ]
    },
    {
        srNO: 2,
        static: true,
        type: "SEARCH_TEXT",
        name: "searchText",
        label: "Search Text",
    },
    {
        srNO: 5,
        static: true,
        type: "DATE",
        name: "dateType",
        label: "Date",
        options: [
            { label: 'Created Date', value: 'createdAt' },
        ]
    },
    {
        srNO: 6,
        static: false,
        removable: true,
        type: "SELECT",
        name: "brandId",
        label: "Brand Name",
    },
    {
        srNO: 7,
        static: false,
        removable: true,
        type: "SELECT",
        name: "categoryId",
        label: "Category Name",
    },
    {
        srNO: 8,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "productName",
        label: "Product Name",
    },
    {
        srNO: 9,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "variantName",
        label: "Variant Name",
    },
]

const SearchBar = (props) => {
    let { vendorRateContractId, vendorRateContract, materialBrands, materialCategories } = props

    const isLoading = useSelector(state => state.vendorRateContractItems.loading)
    const paginationParams = useSelector(state => state.vendorRateContractItems.paginationParams)
    const searchParams = useSelector(state => state.vendorRateContractItems.searchParams)
    const searchOptions = useSelector(state => state.vendorRateContractItems.searchOptions)

    const searchConfig = vendorRateContractItemsConfig

    const dispatch = useDispatch()

    const filterMenu = useRef(null)
    const downloadMenu = useRef(null)
    const searchTextRef = useRef(null)

    useEffect(() => {
        dispatch(setSearchConfig(vendorRateContractItemsConfig))
        dispatch(fetchBuyerCompaniesUsers())
        dispatch(fetchBuyerCompanies())

        if (searchOptions.length === 0) {
            const filteredSearchOptions = searchConfig.filter(eachOption => eachOption.static)
            dispatch(updateSearchOptions(filteredSearchOptions))
        }
    }, [])

    useEffect(() => {
        const materialSearchConfig = vendorRateContractItemsConfig.find(eachSearchConfig => eachSearchConfig.name === "materialId")
        if (!materialSearchConfig && vendorRateContract && vendorRateContract.vendorRCCoverageTypeId === 4){
            vendorRateContractItemsConfig = [...vendorRateContractItemsConfig, {
                srNO: 10,
                static: false,
                removable: true,
                isSearchByOption: true,
                type: "TEXT",
                name: "materialId",
                label: "Material Id",
            },{
                srNO: 11,
                static: false,
                removable: true,
                isSearchByOption: true,
                type: "TEXT",
                name: "productSpecification",
                label: "Product Specification",
            },]
        }
    }, [vendorRateContract])


    useEffect(() => {
        if (searchTextRef.current) {
            searchTextRef.current.focus()
        }
    }, [searchParams.searchBy])

    const addFilterOption = (srNO) => {
        let relatedConfig = searchConfig.find(eachConfig => eachConfig.srNO === srNO)
        if (relatedConfig.name === "brandId"){
            const updatedMaterialBrands = materialBrands.map(eachMaterialBrand => ({...eachMaterialBrand, label: eachMaterialBrand.brandName, value: eachMaterialBrand.brandId}))
            relatedConfig = {...relatedConfig, options: updatedMaterialBrands}
        }
        if (relatedConfig.name === "categoryId"){
            const updatedMaterialCategories = materialCategories.map(eachMaterialCategory => ({...eachMaterialCategory, label: eachMaterialCategory.categoryName, value: eachMaterialCategory.categoryId}))
            relatedConfig = {...relatedConfig, options: updatedMaterialCategories}
        }
        dispatch(updateSearchOptions([...searchOptions, relatedConfig]))
    }

    const removeFilterOption = (srNO) => {
        const relatedSearchOption = searchOptions.find(eachOption => eachOption.srNO === srNO)
        const updatedSearchParams = { ...searchParams }

        if (relatedSearchOption.type === "SEARCH_BY") {
            updatedSearchParams.searchText = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "DATE") {
            updatedSearchParams.fromDate = null
            updatedSearchParams.toDate = null
            updatedSearchParams.range = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "SELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "TEXT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "MULTISELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "AUTO_COMPLETE") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }

        const filteredSearchOptions = searchOptions.filter(eachOption => eachOption.srNO !== srNO)
        dispatch(updateSearchOptions([...filteredSearchOptions]))
    }

    const handleSearchOnChange = (event) => {
        let eventValue = event.target.value

        if (eventValue && (eventValue.userId || eventValue.companyId)) {
            eventValue = eventValue.displayName
        }

        const updatedSearchParams = { ...searchParams, [event.target.name]: eventValue }

        if (eventValue instanceof Date) {
            if(new Date(searchParams[event.target.name])?.getTime() != eventValue.getTime()){
                updatedSearchParams.range = null
            }

            if (event.target.name === "toDate") {
                eventValue.setHours(23, 59, 0, 0)
            }
            if (event.target.name === "fromDate") {
                eventValue.setHours(0, 0, 0, 0)
            }
            eventValue = eventValue.toISOString()

            updatedSearchParams[event.target.name] = eventValue
        }

        if (event.target.name === "searchText") {
            const availableOptions = [...vendorRateContractItemsConfig] 
            const matchText = eventValue.split(";")[0]
            const matchedPattern = getMatchedRegexPattern(matchText, availableOptions, searchParams.searchBy)
            updatedSearchParams.searchBy = matchedPattern
        }
        dispatch(updateSearchParams(updatedSearchParams))
    }

    const handleDateRangeChange = (range) => {
        const { fromDate, toDate } = getDateRange(range)
        
        dispatch(updateSearchParams({ ...searchParams, range, fromDate, toDate }))
    }

    const downloadVendorRCItemsData = async (dataFormat) => {
        const vendorRCItemRes = await searchVendorRateContractItems({...paginationParams, ...searchParams, paginator: false, vendorRateContractIds: [vendorRateContractId]})

        if (vendorRCItemRes.failure){
            return dispatch(raiseToast({ severity: 'error', summary: 'Vendor RC Items', detail: vendorRCItemRes.data.error }))
        }

        let materialDetails = []
        if (vendorRateContract.vendorRCCoverageTypeId === 4){
            const materialIds = vendorRCItemRes.data.data.map(eachVendorRCItem => eachVendorRCItem.materialId)
            const materialDetailsRes = await getMaterialMasterAndTempMaterialIds({materialIds: materialIds})

            if (materialDetailsRes.failure){
                return dispatch(raiseToast({ severity: 'error', summary: 'Material Details', detail: materialDetailsRes.data.error }))
            }

            materialDetails = materialDetailsRes.data
        }

        let vendorRCItemsData = []

        for (let i = 0; i < vendorRCItemRes.data.data.length; i++) {
            const vendorRCItem = vendorRCItemRes.data.data[i]
            let counter = i + 1

            let downloadObj = {
                'SRNO': counter,
                'VENDOR RATE CONTRACT ITEM ID': vendorRCItem.vendorRateContractItemId,
                "STATUS": vendorRCItem.statusId === 1 ? "ACTIVE" : "INACTIVE",
            }

            if (vendorRCItem.materialId){
                const relatedMaterial = materialDetails.find(eachMaterial => eachMaterial.materialId === vendorRCItem.materialId)
                downloadObj["MATERIAL ID"] = vendorRCItem.materialId
                downloadObj["PRODUCT NAME"] = relatedMaterial.productName
                downloadObj["PRODUCT SPECIFICATION"] = relatedMaterial.productSpecification
            }

            if (vendorRCItem.brandId){
                const relatedBrand = materialBrands.find(eachBrand => eachBrand.brandId === vendorRCItem.brandId)
                downloadObj["BRAND"] = relatedBrand.brandName
            }

            if (vendorRCItem.categoryId){
                const relatedCategory = materialCategories.find(eachCategory => eachCategory.categoryId === vendorRCItem.categoryId)
                downloadObj["CATEGORY"] = relatedCategory.categoryName
            }

            if (vendorRCItem.productName){
                downloadObj["PRODUCT NAME"] = vendorRCItem.productName
            }

            if (vendorRCItem.variantName){
                downloadObj["VARIANT NAME"] = vendorRCItem.variantName
            }

            if (vendorRCItem.listPrice){
                downloadObj["LIST PRICE"] = vendorRCItem.listPrice
            }

            if (vendorRCItem.discount || vendorRCItem.discount === 0){
                downloadObj["DISCOUNT"] = vendorRCItem.discount
            }

            if (vendorRCItem.contractPrice){
                downloadObj["CONTRACT PRICE"] = vendorRCItem.contractPrice
            }

            if (vendorRCItem.listPriceDate || [2, 3].includes(vendorRateContract.vendorRCTypeId)){
                downloadObj["LIST PRICE DATE"] = vendorRCItem.listPriceDate
            }

            if (vendorRCItem.leadTime){
                downloadObj["LEAD TIME"] = vendorRCItem.leadTime
            }
           
            vendorRCItemsData.push(downloadObj)
        }

        if (dataFormat === "CSV") {
            let csv = Papa.unparse(vendorRCItemsData, { download: true })
            let blob = new Blob([csv])
            let a = window.document.createElement("a")
            a.href = window.URL.createObjectURL(blob, { type: "text/plain" })
            a.download = "vendorRCItems.csv"
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        }
        if (dataFormat === "XLSX") {
            const ws = utils.json_to_sheet(vendorRCItemsData)
            const wb = utils.book_new()
            utils.book_append_sheet(wb, ws, 'Sheet1')
            const blob = new Blob([write(wb, { bookType: 'xlsx', type: 'array' })])
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'vendorRCItems.xlsx'
            a.click()
            URL.revokeObjectURL(url)
        }
    }

    const downloadFormatOptions = [
        {
            label: "XLSX",
            command: () => downloadVendorRCItemsData("XLSX")
        },
        {
            label: "CSV",
            command: () => downloadVendorRCItemsData("CSV")
        }
    ]

    const addFilterOptions = getAddFilterOptions(searchConfig, searchOptions, searchParams, addFilterOption)

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch()
        }
    }

    const handleSearch = () => {
        let errorFound = false

        // if (searchParams.searchText === "" && searchParams.searchBy !== "all") {
        //     dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: "Please enter search text" }))
        //     errorFound = true
        // } 
        // else if (searchParams.searchText && searchParams.searchBy !== "all"){
        //     const relatedSearchOption = searchConfig.find(eachOption => eachOption.name === searchParams.searchBy)
        //     if(relatedSearchOption.isValid && !relatedSearchOption.isValid(searchParams.searchText.split(";")[0])){
        //         dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: `Please enter valid ${normalizeLabel(searchParams.searchBy).toLowerCase()}` }))
        //         errorFound = true
        //     }
        // }
        
        searchOptions.map(eachOption => { 
            if (["TEXT", "SELECT", "AUTO_COMPLETE"].includes(eachOption.type)) {
                if (!searchParams[eachOption.name] && searchParams[eachOption.name] !== false) {
                    const errorMsgText = normalizeLabel(eachOption.label)
    
                    dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter ${errorMsgText.toLowerCase()}` }))
                    errorFound = true
                } else {
                    if(eachOption.isValid && !eachOption.isValid(searchParams[eachOption.name].split(";")[0])){
                        const errorMsgText = normalizeLabel(eachOption.label)
                        dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter valid ${errorMsgText.toLowerCase()}` }))
                        errorFound = true
                    }
                }
            } 
        })

        const updatedSearchParams = { ...searchParams }

        if (updatedSearchParams.searchBy !== "all") {
            updatedSearchParams[updatedSearchParams.searchBy] = updatedSearchParams.searchText.trim()
        }

        if (searchParams.searchText) {
            if (searchParams.searchBy === 'all') {
                return dispatch(raiseToast({ severity: 'error', detail: `Please select a filter!` }))
            }
        }

        const vendorRateContractItemsParams = {
            ...paginationParams,
            ...updatedSearchParams,
            pageNo: 0,
            paginator: true,
            vendorRCCoverageTypeId: vendorRateContract.vendorRCCoverageTypeId,
            vendorRateContractIds: [vendorRateContractId]
        }

        if (!errorFound) {
            dispatch(fetchVendorRateContractItems(vendorRateContractItemsParams))
        }
    }

    return (
        <div className="p-card" style={{ border: "1px solid #e9ecef" }} onKeyDown={handleSearchKeyPress}>
            <div className="grid">
                {
                    searchOptions.map(eachOption => {

                        if (eachOption.type === "SEARCH_TEXT") {

                            let searchByOptions = [{ label: 'No Filters', value: 'all' }]
                            searchConfig.map(eachSearchBy => {
                                if (eachSearchBy.isSearchByOption) {
                                    const optionInUse = searchOptions.find(eachSearchOption => eachSearchOption.srNO === eachSearchBy.srNO)
                                    if(!optionInUse){
                                        searchByOptions.push({ label: eachSearchBy.label, value: eachSearchBy.name })
                                    }
                                }
                            })

                            return (
                                <>
                                    <div className="flex flex-column col-12">
                                        <TextInputField
                                            name="searchText"
                                            labelText="Search Text"
                                            placeholder="No Filter"
                                            refName={searchTextRef}
                                            value={searchParams["searchText"]}
                                            onChange={handleSearchOnChange}
                                        />
                                    </div>
                                    {searchParams.searchText && (
                                        <div className="flex flex-column col-12">
                                            <DropDownField
                                                name="searchBy"
                                                labelText="Search By"
                                                options={searchByOptions}
                                                value={searchParams["searchBy"]}
                                                onChange={handleSearchOnChange}
                                            />
                                        </div>
                                    )}
                                </>
                            )
                        }

                        if (eachOption.type === "DATE") {
                            return (
                                <>
                                    <DateFilter 
                                        eachOption={eachOption} 
                                        searchParams={searchParams} 
                                        dateOptions={["MTD", "PM", "L15", "L30"]}
                                        handleDateRangeChange={handleDateRangeChange} 
                                        handleSearchOnChange={handleSearchOnChange}
                                        removeFilterOption={removeFilterOption}
                                    />
                                </>
                            )
                        }

                        if (eachOption.type === "MULTISELECT") {
                            return (
                                <>
                                    {!eachOption.static ?
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                        :
                                        null
                                    }
                                    <div className="flex flex-column col-12 custom-multiselect">
                                        <MultiSelectField name={eachOption.name} labelText={eachOption.label} options={eachOption.options} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }

                        if (eachOption.type === "SELECT") {
                            const updatedOptions = eachOption.options.map(eachSelectOption => ({ label: eachSelectOption.label, value: eachSelectOption.value }))
                            return (
                                <>
                                    {!eachOption.static &&
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                    }
                                    <div className="flex flex-column col-12">
                                        <DropDownField name={eachOption.name} options={updatedOptions} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }

                        if (eachOption.type === "TEXT") {
                            return (
                                <>
                                    {!eachOption.static &&
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)}></i>
                                        </div>
                                    }
                                    <div className="flex flex-column col-12">
                                        <TextInputField name={eachOption.name} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }

                        if (eachOption.type === "AUTO_COMPLETE") {
                            return (
                                <>
                                    {!eachOption.static &&
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                    }
                                    {
                                        <div className="flex flex-column col-12">
                                            <AutoCompleteField
                                                value={searchParams[eachOption.name]}
                                                field="displayName"
                                                labelText={eachOption.label}
                                                onChange={handleSearchOnChange}
                                                name={eachOption.name}
                                                dropdown
                                            />
                                        </div>
                                    }
                                </>
                            )
                        }
                    }
                )}

                <div className="flex col-12 justify-content-end">
                    <Menu model={addFilterOptions} popup menuRef={filterMenu} />
                    <Button icon="pi pi-filter" className="h-2rem p-2 action-btn hover:surface-300 hover:text-700" label="Add Filter" onClick={(e) => filterMenu.current.toggle(e)} />
                </div>

                <div className="flex flex-column col-12 mb-0 pb-0">
                    <div className="grid">
                        <div className="flex col-12 mb-0 gap-1 pb-0" style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}>
                            <Button label="Search" style={{ flexGrow: 1 }} className="py-2" onClick={handleSearch} disabled={isLoading}/>
                            <Menu model={downloadFormatOptions} popup menuRef={downloadMenu} />
                            <Button icon="pi pi-download" className="py-2 px-3" onClick={(e) => downloadMenu.current.toggle(e)} permissions={[219]}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )

}

export default SearchBar;